import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogIn, Eye, EyeOff, AlertCircle, ArrowRight, Phone, KeyRound,
  ChevronLeft, Users, School2, Headphones, Building2, Shield, Activity
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import api from '../../services/api';

type LoginStep = 'login' | 'rolePicker' | 'forgot' | 'otp' | 'newPassword';

interface RoleConfig {
  Icon: any;
  label: string;
  description: string;
  bgColor: string;
  hoverBg: string;
  borderColor: string;
  iconBg: string;
  iconColor: string;
  textColor: string;
  route: string;
  storeRole: string;
}

export default function UnifiedLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser, setToken, setRefreshToken, setTokenExpiresAt, setRequiresPasswordChange, logout } = useAuthStore();

  // Clear any stale session so user always gets fresh login
  useEffect(() => { logout(); }, []);

  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Role picker state
  const [step, setStep] = useState<LoginStep>('login');
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [loginData, setLoginData] = useState<any>(null);

  // Forgot password state
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const getRoleConfig = (role: string): RoleConfig => {
    const r = role.toLowerCase();
    if (r === 'parent') return {
      Icon: Users, label: t('login.role_parent'), description: t('login.role_parent_desc'),
      bgColor: 'bg-blue-50/50', hoverBg: 'hover:bg-blue-50', borderColor: 'border-blue-200',
      iconBg: 'bg-blue-100', iconColor: 'text-blue-600', textColor: 'text-blue-900',
      route: '/parent/dashboard', storeRole: 'parent',
    };
    if (r === 'teacher') return {
      Icon: School2, label: t('login.role_teacher'), description: t('login.role_teacher_desc'),
      bgColor: 'bg-violet-50/50', hoverBg: 'hover:bg-violet-50', borderColor: 'border-violet-200',
      iconBg: 'bg-violet-100', iconColor: 'text-violet-600', textColor: 'text-violet-900',
      route: '/teacher/login', storeRole: 'teacher',
    };
    if (r === 'staff' || r === 'assistant') return {
      Icon: School2, label: t('login.role_staff'), description: t('login.role_staff_desc'),
      bgColor: 'bg-green-50/50', hoverBg: 'hover:bg-green-50', borderColor: 'border-green-200',
      iconBg: 'bg-green-100', iconColor: 'text-green-600', textColor: 'text-green-900',
      route: '/tablet/dashboard', storeRole: 'tablet',
    };
    if (r === 'callcenter') return {
      Icon: Headphones, label: t('login.role_callcenter'), description: t('login.role_callcenter_desc'),
      bgColor: 'bg-teal-50/50', hoverBg: 'hover:bg-teal-50', borderColor: 'border-teal-200',
      iconBg: 'bg-teal-100', iconColor: 'text-teal-600', textColor: 'text-teal-900',
      route: '/callcenter/dashboard', storeRole: 'admin',
    };
    if (r === 'systemadmin' || r === 'sysadmin') return {
      Icon: Shield, label: t('login.role_sysadmin'), description: t('login.role_sysadmin_desc'),
      bgColor: 'bg-red-50/50', hoverBg: 'hover:bg-red-50', borderColor: 'border-red-200',
      iconBg: 'bg-red-100', iconColor: 'text-red-600', textColor: 'text-red-900',
      route: '/sysadmin/dashboard', storeRole: 'sysadmin',
    };
    if (r === 'simulation') return {
      Icon: Activity, label: t('login.role_simulation'), description: t('login.role_simulation_desc'),
      bgColor: 'bg-purple-50/50', hoverBg: 'hover:bg-purple-50', borderColor: 'border-purple-200',
      iconBg: 'bg-purple-100', iconColor: 'text-purple-600', textColor: 'text-purple-900',
      route: '/simulation/dashboard', storeRole: 'sysadmin',
    };
    if (r === 'admin') return {
      Icon: Building2, label: t('login.role_admin'), description: t('login.role_admin_desc'),
      bgColor: 'bg-amber-50/50', hoverBg: 'hover:bg-amber-50', borderColor: 'border-amber-200',
      iconBg: 'bg-amber-100', iconColor: 'text-amber-600', textColor: 'text-amber-900',
      route: '/admin/dashboard', storeRole: 'admin',
    };
    if (r === 'networkmanager') return {
      Icon: Building2, label: t('login.role_network'), description: t('login.role_network_desc'),
      bgColor: 'bg-orange-50/50', hoverBg: 'hover:bg-orange-50', borderColor: 'border-orange-200',
      iconBg: 'bg-orange-100', iconColor: 'text-orange-600', textColor: 'text-orange-900',
      route: '/admin/dashboard', storeRole: 'admin',
    };
    if (r === 'citymanager') return {
      Icon: Building2, label: t('login.role_city'), description: t('login.role_city_desc'),
      bgColor: 'bg-orange-50/50', hoverBg: 'hover:bg-orange-50', borderColor: 'border-orange-200',
      iconBg: 'bg-orange-100', iconColor: 'text-orange-600', textColor: 'text-orange-900',
      route: '/admin/dashboard', storeRole: 'admin',
    };
    if (r === 'inspector') return {
      Icon: Building2, label: t('login.role_inspector'), description: t('login.role_inspector_desc'),
      bgColor: 'bg-orange-50/50', hoverBg: 'hover:bg-orange-50', borderColor: 'border-orange-200',
      iconBg: 'bg-orange-100', iconColor: 'text-orange-600', textColor: 'text-orange-900',
      route: '/admin/dashboard', storeRole: 'admin',
    };
    // Fallback for any unknown role
    return {
      Icon: Building2, label: role, description: '',
      bgColor: 'bg-gray-50/50', hoverBg: 'hover:bg-gray-50', borderColor: 'border-gray-200',
      iconBg: 'bg-gray-100', iconColor: 'text-gray-600', textColor: 'text-gray-900',
      route: '/admin/dashboard', storeRole: 'admin',
    };
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Try admin login first (covers all roles now)
      const response = await api.post('/auth/admin-login', { username, password });
      const data = response.data;

      // Handle ApiResponse wrapping: data might be { success, data: { success, accessToken, user } }
      const authResult = data?.data || data;

      if (authResult?.success === false) {
        // If admin login fails, try parent login
        await tryParentLogin();
        return;
      }

      processLoginResult(authResult);
    } catch (err: any) {
      // Try parent login as fallback
      await tryParentLogin();
    } finally {
      setLoading(false);
    }
  };

  const tryParentLogin = async () => {
    try {
      const parentResponse = await api.post('/auth/parent-login', { phone: username, password });
      const parentData = parentResponse.data;
      const authResult = parentData?.data || parentData;
      if (authResult?.success !== false) {
        processLoginResult(authResult);
        return;
      }
    } catch {
      // Both failed
    }
    setError(t('login.error'));
  };

  const processLoginResult = (authResult: any) => {
    const token = authResult.accessToken || authResult.token;
    const user = authResult.user;
    const roles: string[] = user?.roles || [user?.role || user?.serverRole].filter(Boolean);

    // SysAdmin gets simulation option
    const hasSysAdmin = roles.some(r => r.toLowerCase() === 'sysadmin' || r.toLowerCase() === 'systemadmin');
    if (hasSysAdmin && !roles.includes('simulation')) {
      roles.push('simulation');
    }

    if (roles.length > 1) {
      // Multiple roles → show role picker
      setAvailableRoles(roles);
      setLoginData({ token, user, authResult });
      setStep('rolePicker');
      return;
    }

    // Single role → route directly
    completeLogin(token, user, authResult, roles[0] || user?.role);
  };

  const completeLogin = (token: string, user: any, authResult: any, selectedRole: string) => {
    const config = getRoleConfig(selectedRole);
    const userWithRole = {
      ...user,
      role: config.storeRole,
      // שמור serverRole מהבקנד לזיהוי מוקד חרום
      serverRole: selectedRole.toLowerCase() === 'callcenter' ? 'CallCenter' : user?.serverRole,
    };

    setUser(userWithRole);
    setToken(token);
    if (authResult.refreshToken) setRefreshToken(authResult.refreshToken);
    if (authResult.expiresAt) setTokenExpiresAt(authResult.expiresAt);
    if (authResult.requiresPasswordChange || authResult.mustChangePassword) {
      setRequiresPasswordChange(true);
    }

    navigate(config.route);
  };

  const handleRoleSelect = (role: string) => {
    if (!loginData) return;
    const { token, user, authResult } = loginData;
    completeLogin(token, user, authResult, role);
  };

  const handleSendOtp = async () => {
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/send-otp', { phone });
      setStep('otp');
    } catch {
      setError(t('login.otpError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const response = await api.post('/auth/verify-otp', { phone, otp });
      if (response.data.success) {
        setStep('newPassword');
      } else {
        setError(t('login.otpInvalid'));
      }
    } catch {
      setError(t('login.otpInvalid'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) { setError(t('login.passwordMismatch')); return; }
    if (newPassword.length < 8) { setError(t('login.passwordTooShort')); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { phone, otp, newPassword });
      setSuccessMessage(t('login.passwordResetSuccess'));
      setTimeout(() => {
        setStep('login');
        setSuccessMessage('');
        setPhone(''); setOtp(''); setNewPassword(''); setConfirmPassword('');
      }, 2000);
    } catch {
      setError(t('login.resetError'));
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setError('');
    if (step === 'rolePicker') setStep('login');
    else if (step === 'otp') setStep('forgot');
    else if (step === 'newPassword') setStep('otp');
    else setStep('login');
  };

  const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition-all';

  const getTitle = () => {
    if (step === 'rolePicker') return t('login.selectRoleTitle');
    if (step === 'login') return t('login.title');
    return t('login.forgotPasswordTitle');
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-cyan-50/30 pt-20 pb-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/">
            <img src="/kipikid-logo.png" alt="KipiKid" className="w-14 h-14 mx-auto mb-3" />
          </Link>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">{getTitle()}</h1>
          {step === 'rolePicker' && (
            <p className="text-xs text-gray-500 mt-1">{t('login.selectRole')}</p>
          )}
          {step !== 'login' && step !== 'rolePicker' && (
            <p className="text-xs text-gray-500 mt-1">{t(`login.forgotStep_${step}`)}</p>
          )}
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-5 md:p-6">
          <AnimatePresence mode="wait">

            {/* ==================== LOGIN ==================== */}
            {step === 'login' && (
              <motion.div key="login" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <form onSubmit={handleLogin}>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('login.username')}</label>
                    <input
                      type="text" required autoComplete="username"
                      placeholder={t('login.usernamePlaceholder')}
                      value={username} onChange={e => setUsername(e.target.value)}
                      className={inputClass} dir="ltr"
                    />
                  </div>
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('login.password')}</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'} required autoComplete="current-password"
                        value={password} onChange={e => setPassword(e.target.value)}
                        className={`${inputClass} ltr:pr-10 rtl:pl-10`} dir="ltr"
                      />
                      <button
                        type="button" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}
                        className="absolute top-1/2 -translate-y-1/2 ltr:right-3 rtl:left-3 p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                        aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="mb-4 text-end">
                    <button type="button" onClick={() => { setStep('forgot'); setError(''); }}
                      className="text-xs text-cyan-600 hover:text-cyan-700 hover:underline transition-colors">
                      {t('login.forgotPassword')}
                    </button>
                  </div>
                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-xs mb-3 p-2 bg-red-50 rounded-lg">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{error}</span>
                    </div>
                  )}
                  <button type="submit" disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-cyan-600 text-white font-semibold rounded-xl text-sm hover:bg-cyan-700 transition-all shadow-md shadow-cyan-600/20 disabled:opacity-50">
                    {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : <><LogIn className="w-4 h-4" />{t('login.submit')}</>}
                  </button>
                </form>
                <p className="text-center text-[11px] text-gray-400 mt-4 leading-relaxed">{t('login.infoText')}</p>
              </motion.div>
            )}

            {/* ==================== ROLE PICKER ==================== */}
            {step === 'rolePicker' && (
              <motion.div key="rolePicker" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.25 }}>
                <div className="grid grid-cols-1 gap-2.5">
                  {availableRoles.map(role => {
                    const config = getRoleConfig(role);
                    return (
                      <button
                        key={role}
                        onClick={() => handleRoleSelect(role)}
                        className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all hover:shadow-md hover:-translate-y-0.5 ${config.borderColor} ${config.bgColor} ${config.hoverBg}`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${config.iconBg}`}>
                          <config.Icon className={`w-5 h-5 ${config.iconColor}`} />
                        </div>
                        <div className="text-start flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${config.textColor}`}>{config.label}</p>
                          <p className="text-[11px] text-gray-400 truncate">{config.description}</p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-300 shrink-0" />
                      </button>
                    );
                  })}
                </div>
                <button onClick={goBack}
                  className="w-full flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors mt-4">
                  <ChevronLeft className="w-3.5 h-3.5" />{t('login.backToLogin')}
                </button>
              </motion.div>
            )}

            {/* ==================== FORGOT — PHONE ==================== */}
            {step === 'forgot' && (
              <motion.div key="forgot" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('login.phoneNumber')}</label>
                  <div className="relative">
                    <input type="tel" required placeholder="05X-XXXXXXX"
                      value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className={`${inputClass} ltr:pl-10 rtl:pr-10`} dir="ltr" />
                    <Phone className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 w-4 h-4 text-gray-400" />
                  </div>
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-xs mb-3 p-2 bg-red-50 rounded-lg">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{error}</span>
                  </div>
                )}
                <button onClick={handleSendOtp} disabled={loading || phone.length !== 10}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-cyan-600 text-white font-semibold rounded-xl text-sm hover:bg-cyan-700 transition-all shadow-md shadow-cyan-600/20 disabled:opacity-50 mb-3">
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <>{t('login.sendOtp')}<ArrowRight className="w-4 h-4" /></>}
                </button>
                <button onClick={goBack}
                  className="w-full flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors">
                  <ChevronLeft className="w-3.5 h-3.5" />{t('login.backToLogin')}
                </button>
              </motion.div>
            )}

            {/* ==================== OTP VERIFY ==================== */}
            {step === 'otp' && (
              <motion.div key="otp" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <p className="text-xs text-gray-500 mb-3 text-center">
                  {t('login.otpSentTo')} <span className="font-semibold text-gray-700 dir-ltr inline-block">{phone}</span>
                </p>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('login.otpCode')}</label>
                  <div className="relative">
                    <input type="text" required placeholder="XXXXXX" maxLength={6}
                      value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className={`${inputClass} text-center text-lg tracking-[0.3em] font-mono ltr:pl-10 rtl:pr-10`} dir="ltr" />
                    <KeyRound className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 w-4 h-4 text-gray-400" />
                  </div>
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-xs mb-3 p-2 bg-red-50 rounded-lg">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{error}</span>
                  </div>
                )}
                <button onClick={handleVerifyOtp} disabled={loading || otp.length < 4}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-cyan-600 text-white font-semibold rounded-xl text-sm hover:bg-cyan-700 transition-all shadow-md shadow-cyan-600/20 disabled:opacity-50 mb-3">
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    : <>{t('login.verifyOtp')}<ArrowRight className="w-4 h-4" /></>}
                </button>
                <button onClick={goBack}
                  className="w-full flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors">
                  <ChevronLeft className="w-3.5 h-3.5" />{t('login.back')}
                </button>
              </motion.div>
            )}

            {/* ==================== NEW PASSWORD ==================== */}
            {step === 'newPassword' && (
              <motion.div key="newPassword" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                {successMessage ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-green-700">{successMessage}</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('login.newPassword')}</label>
                      <div className="relative">
                        <input type={showNewPassword ? 'text' : 'password'} required
                          value={newPassword} onChange={e => setNewPassword(e.target.value)}
                          className={`${inputClass} ltr:pr-10 rtl:pl-10`} dir="ltr" />
                        <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} tabIndex={-1}
                          className="absolute top-1/2 -translate-y-1/2 ltr:right-3 rtl:left-3 p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('login.confirmPassword')}</label>
                      <input type="password" required value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)} className={inputClass} dir="ltr" />
                    </div>
                    <div className="mb-4 p-2.5 bg-gray-50 rounded-lg">
                      <p className="text-[10px] font-medium text-gray-500 mb-1.5">{t('login.passwordRules')}</p>
                      <ul className="text-[10px] text-gray-400 space-y-0.5">
                        <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>✓ {t('login.rule_length')}</li>
                        <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>✓ {t('login.rule_upper')}</li>
                        <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>✓ {t('login.rule_lower')}</li>
                        <li className={/\d/.test(newPassword) ? 'text-green-600' : ''}>✓ {t('login.rule_digit')}</li>
                        <li className={/[!@#$%^&*(),.?":{}|<>]/.test(newPassword) ? 'text-green-600' : ''}>✓ {t('login.rule_special')}</li>
                      </ul>
                    </div>
                    {error && (
                      <div className="flex items-center gap-2 text-red-600 text-xs mb-3 p-2 bg-red-50 rounded-lg">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" /><span>{error}</span>
                      </div>
                    )}
                    <button onClick={handleResetPassword} disabled={loading || !newPassword || !confirmPassword}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-cyan-600 text-white font-semibold rounded-xl text-sm hover:bg-cyan-700 transition-all shadow-md shadow-cyan-600/20 disabled:opacity-50 mb-3">
                      {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        : t('login.resetPassword')}
                    </button>
                    <button onClick={goBack}
                      className="w-full flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors">
                      <ChevronLeft className="w-3.5 h-3.5" />{t('login.back')}
                    </button>
                  </>
                )}
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </section>
  );
}
